import DotsIcon from '@/assets/icons/dots.svg?react';
import { TDate } from '@/components/i18n/date.i18n.component';
import { TTime } from '@/components/i18n/time.i18n.component';
import Button from '@/components/ui/button/button.component';
import {
  Card,
  CardDataRow,
  CardDivider,
  CardHeader,
  CardHeaderDescription,
  CardHeaderTitle,
  CardUpdatedAt,
} from '@/components/ui/card/card.component';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown/dropdown.component';
import { useIntl } from 'react-intl';
import { generalCardTranslations } from './general-card.translations';

type GeneralCardProps = {
  context: 'product' | 'product-group' | 'template';
  id: string;
  title: string;
  description: string;
  onCardClick: (id: string) => void;
  rows?: { name: string; label: React.ReactNode; value: React.ReactNode }[];
  button?: {
    name: string;
    label: React.ReactNode;
    handleClick: () => void;
  };
  updatedAt?: string;
  menu?: {
    name: string;
    disabled?: boolean;
    label: React.ReactNode;
    handleClick: () => void;
    red?: boolean;
  }[];
};

export const GeneralCard = ({
  context,
  id,
  title,
  description,
  rows,
  updatedAt,
  onCardClick: handleCardClick,
  button,
  menu,
}: GeneralCardProps) => {
  const t = useIntl();
  return (
    <Card data-testid={`${context}-card-${id}`}>
      <CardHeader>
        <div className="flex flex-row justify-between items-center">
          <CardHeaderTitle
            title={title}
            onClick={() => {
              handleCardClick(id);
            }}
            data-testid={`${context}-card-${id}-name-button`}
            valueTestId={`${context}-card-${id}-name-value`}
          >
            {title}
          </CardHeaderTitle>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild data-testid={`${context}-card-${id}-dropdown-trigger`}>
              <Button variant="icon">
                <DotsIcon className="cursor-pointer text-secondary-text" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {menu?.map((item) => (
                <DropdownMenuItem
                  key={item.name}
                  disabled={item.disabled}
                  onClick={item.handleClick}
                  className={item.red ? 'text-system-error' : ''}
                  data-testid={`${context}-card-${id}-dropdown-trigger-${item.name}`}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardHeaderDescription title={description} data-testid={`${context}-card-${id}-description`}>
          {description}
        </CardHeaderDescription>
      </CardHeader>
      <CardDivider />
      <CardHeader>
        {rows?.map((row) => <CardDataRow {...row} key={row.name} context={context} id={id} />)}
        {updatedAt && (
          <CardUpdatedAt data-testid={`${context}-card-${id}-updated-at`}>
            {t.formatMessage(generalCardTranslations.updatedOn, {
              date: <TDate value={new Date(updatedAt)} />,
              time: <TTime value={new Date(updatedAt)} />,
            })}
          </CardUpdatedAt>
        )}
        {button && (
          <Button
            variant="secondary"
            data-testid={`${context}-card-${id}-${button.name}-button`}
            onClick={button.handleClick}
          >
            {button.label}
          </Button>
        )}
      </CardHeader>
    </Card>
  );
};
